import React, { useState, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import api, { formatVND } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Calendar, Copy } from "lucide-react";
import DateInputVN, { todayISO, plusOneMonthISO } from "@/components/DateInputVN";

const DAY_NAMES = ["Chủ nhật", "Thứ hai", "Thứ ba", "Thứ tư", "Thứ năm", "Thứ sáu", "Thứ bảy"];

export default function ScheduleModal({ open, onOpenChange, group, onSaved }) {
  const queryClient = useQueryClient();
  const [courts, setCourts] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [weeklySchedule, setWeeklySchedule] = useState(
    DAY_NAMES.map((_, i) => ({ day_of_week: i, enabled: false, start_time: "", end_time: "" }))
  );
  const [selectedCourts, setSelectedCourts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/courts").then(({ data }) => {
        setCourts((data.courts || []).filter(c => c.court_type === "regular"));
      });
      if (group?.schedule) {
        const s = group.schedule;
        setStartDate(s.start_date || "");
        setEndDate(s.end_date || "");
        setWeeklySchedule(
          DAY_NAMES.map((_, i) => {
            const existing = (s.weekly_schedule || []).find(w => w.day_of_week === i);
            return existing || { day_of_week: i, enabled: false, start_time: "", end_time: "" };
          })
        );
        setSelectedCourts(s.court_ids || []);
      } else {
        const today = todayISO();
        setStartDate(today);
        setEndDate(plusOneMonthISO(today));
        setWeeklySchedule(DAY_NAMES.map((_, i) => ({ day_of_week: i, enabled: false, start_time: "", end_time: "" })));
        setSelectedCourts([]);
      }
    }
  }, [open, group]);

  const toggleDay = (idx) => {
    setWeeklySchedule(ws => ws.map((w, i) => i === idx ? { ...w, enabled: !w.enabled } : w));
  };

  const setDayTime = (idx, field, val) => {
    setWeeklySchedule(ws => ws.map((w, i) => i === idx ? { ...w, [field]: val } : w));
  };

  const applyToAll = (idx) => {
    const src = weeklySchedule[idx];
    if (!src.start_time || !src.end_time) return;
    setWeeklySchedule(ws => ws.map(w => w.enabled ? { ...w, start_time: src.start_time, end_time: src.end_time } : w));
    toast.success("Đã áp dụng giờ cho tất cả ngày đã chọn");
  };

  const toggleCourt = (courtId) => {
    setSelectedCourts(prev => prev.includes(courtId) ? prev.filter(c => c !== courtId) : [...prev, courtId]);
  };

  const enabledDays = weeklySchedule.filter(w => w.enabled);

  const summary = useMemo(() => {
    if (!startDate || !endDate || enabledDays.length === 0 || selectedCourts.length === 0) return null;
    try {
      const sd = new Date(startDate + "T00:00:00");
      const ed = new Date(endDate + "T23:59:59");
      if (ed <= sd) return null;
      const enabledDows = new Set(enabledDays.map(w => w.day_of_week));
      let sessions = 0;
      const cur = new Date(sd);
      while (cur <= ed) {
        if (enabledDows.has(cur.getDay())) sessions++;
        cur.setDate(cur.getDate() + 1);
      }
      const totalBookings = sessions * selectedCourts.length;
      return { sessions, totalBookings, daysPerWeek: enabledDays.length, courts: selectedCourts.length };
    } catch {
      return null;
    }
  }, [startDate, endDate, enabledDays, selectedCourts]);

  const handleSubmit = async (confirmFlagsRaw) => {
    // Defensive: strip non-primitive fields to avoid circular JSON if someone passes an event
    const safeFlags = (confirmFlagsRaw && typeof confirmFlagsRaw === "object" && !confirmFlagsRaw.nativeEvent) ? confirmFlagsRaw : {};
    const confirmFlags = {
      confirm_delete_with_deposit: !!safeFlags.confirm_delete_with_deposit,
    };
    if (!startDate || !endDate) { toast.error("Vui lòng chọn ngày bắt đầu và kết thúc"); return; }
    if (enabledDays.length === 0) { toast.error("Vui lòng chọn ít nhất 1 ngày trong tuần"); return; }
    const invalidDay = enabledDays.find(w => !w.start_time || !w.end_time);
    if (invalidDay) { toast.error(`Vui lòng nhập giờ cho ${DAY_NAMES[invalidDay.day_of_week]}`); return; }
    if (selectedCourts.length === 0) { toast.error("Vui lòng chọn ít nhất 1 sân"); return; }

    const body = {
      start_date: startDate,
      end_date: endDate,
      weekly_schedule: weeklySchedule,
      court_ids: selectedCourts,
      confirm_delete_with_deposit: false,
      force_override: true,  // mặc định force override (theo yêu cầu user 4b)
      ...confirmFlags,
    };

    // Step 1: preview (unless already confirmed)
    if (!confirmFlags.confirm_delete_with_deposit) {
      try {
        const { data: preview } = await api.post(`/groups/${group.id}/schedule/preview`, body);
        if (preview.deposited > 0) {
          const msg = `Có ${preview.deposited} booking cũ của hội này có đặt cọc (${preview.existing_group_auto} booking cũ sẽ bị xoá). Tiếp tục?`;
          if (!window.confirm(msg)) return;
          confirmFlags.confirm_delete_with_deposit = true;
        }
        if (preview.conflict_count > 0) {
          const sample = preview.conflicts.slice(0, 5)
            .map(c => `• ${c.date} ${c.start_time}-${c.end_time} ${c.court_name} (${c.customer_name || "?"})${c.has_deposit ? " [có đặt cọc]" : ""}`)
            .join("\n");
          const msg = `Có ${preview.conflict_count} booking của khách khác sẽ BỊ XOÁ (force override):\n\n${sample}${preview.conflict_count > 5 ? `\n... và ${preview.conflict_count - 5} booking khác` : ""}\n\nTiếp tục?`;
          if (!window.confirm(msg)) return;
        }
      } catch (err) {
        const detail = err.response?.data?.detail;
        const msg = typeof detail === "string" ? detail : (detail?.message || err.message || "Không xem trước được");
        toast.error(msg);
        return;
      }
    }

    // Step 2: apply
    setSaving(true);
    try {
      const finalBody = {
        ...body,
        confirm_delete_with_deposit: confirmFlags.confirm_delete_with_deposit || false,
        force_override: true,
      };
      const { data } = await api.post(`/groups/${group.id}/schedule`, finalBody);
      const parts = [`Đã tạo ${data.total_bookings_created} booking`];
      if (data.deleted_old_group_bookings > 0) parts.push(`xoá ${data.deleted_old_group_bookings} lịch cũ của hội`);
      if (data.deleted_conflicts > 0) parts.push(`ghi đè ${data.deleted_conflicts} booking khác`);
      if (data.skipped > 0) parts.push(`bỏ qua ${data.skipped} slot (đã qua giờ hoặc trùng lịch)`);
      toast.success(parts.join(", "));
      // Invalidate caches để timeline Lịch sân và stats refetch dữ liệu mới
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.detail?.code === "HAS_DEPOSITS") {
        const d = err.response.data.detail;
        if (window.confirm(`${d.message} (${d.deposited_count}/${d.total_old} có cọc)`)) {
          handleSubmit({ confirm_delete_with_deposit: true });
          return;
        }
      } else {
        const detail = err.response?.data?.detail;
        const msg = typeof detail === "string" ? detail : (detail?.message || err.message || "Lỗi khi tạo lịch");
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="schedule-modal">
        <DialogHeader>
          <DialogTitle className="text-[#F8FAFC] font-heading flex items-center gap-2">
            <Calendar className="w-5 h-5 text-teal-400" />
            Lịch biểu hội — {group?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Date range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Ngày bắt đầu *</label>
              <DateInputVN value={startDate} onChange={setStartDate}
                className="bg-[#0f172a] border-[#334155] text-slate-200" data-testid="schedule-start-date" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Ngày kết thúc *</label>
              <DateInputVN value={endDate} onChange={setEndDate}
                className="bg-[#0f172a] border-[#334155] text-slate-200" data-testid="schedule-end-date" />
            </div>
          </div>

          {/* Weekly schedule */}
          <div>
            <label className="text-xs text-slate-400 mb-2 block">Ngày & Giờ trong tuần *</label>
            <div className="space-y-2">
              {weeklySchedule.map((ws, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <button type="button" onClick={() => toggleDay(idx)}
                    className={`w-24 py-1.5 rounded text-xs font-medium border transition-colors shrink-0 ${ws.enabled ? "bg-lime-500/20 text-lime-400 border-lime-500/30" : "bg-transparent text-slate-500 border-[#334155]"}`}
                    data-testid={`day-toggle-${idx}`}>
                    {DAY_NAMES[idx]}
                  </button>
                  {ws.enabled && (
                    <>
                      <Input type="time" value={ws.start_time} onChange={(e) => setDayTime(idx, "start_time", e.target.value)}
                        className="bg-[#0f172a] border-[#334155] text-slate-200 w-24 h-8 text-xs" />
                      <span className="text-slate-500 text-xs">-</span>
                      <Input type="time" value={ws.end_time} onChange={(e) => setDayTime(idx, "end_time", e.target.value)}
                        className="bg-[#0f172a] border-[#334155] text-slate-200 w-24 h-8 text-xs" />
                      <button type="button" onClick={() => applyToAll(idx)}
                        className="p-1 rounded text-slate-500 hover:text-teal-400 transition-colors" title="Áp dụng cho tất cả">
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Courts selection */}
          <div>
            <label className="text-xs text-slate-400 mb-2 block">Sân đặt *</label>
            <div className="grid grid-cols-2 gap-2">
              {courts.map(c => (
                <button key={c.id} type="button" onClick={() => toggleCourt(c.id)}
                  className={`py-2 px-3 rounded-md text-sm border transition-colors text-left ${selectedCourts.includes(c.id) ? "bg-lime-500/20 text-lime-400 border-lime-500/30" : "bg-transparent text-slate-400 border-[#334155] hover:border-slate-400"}`}
                  data-testid={`court-select-${c.id}`}>
                  <div className="font-medium">{c.name}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Summary */}
          {summary && (
            <div className="bg-lime-500/10 border border-lime-500/20 rounded-lg p-3 space-y-1" data-testid="schedule-summary">
              <div className="flex items-center gap-2 text-sm text-lime-400 font-medium">
                <Calendar className="w-4 h-4" />
                Sẽ tạo đặt sân cho {summary.totalBookings} buổi
              </div>
              <div className="text-xs text-lime-400/70">
                {summary.daysPerWeek} ngày/tuần | {summary.courts} sân | {startDate && new Date(startDate + "T00:00:00").toLocaleDateString("vi-VN")} → {endDate && new Date(endDate + "T00:00:00").toLocaleDateString("vi-VN")}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-400">Hủy</Button>
          <Button onClick={() => handleSubmit()} disabled={saving} className="bg-teal-500 hover:bg-teal-600 text-[#0f172a] font-semibold" data-testid="schedule-save-btn">
            {saving ? "Đang tạo..." : "Lưu & Tạo lịch đặt sân"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
